import React, { useState, useEffect } from "react";
import {
  FaDesktop,
  FaMobile,
  FaTabletAlt,
  FaLaptop,
  FaTimes,
  FaCheck,
} from "react-icons/fa";
import {
  listDevicesApi,
  removeDeviceApi,
  setCurrentDeviceApi,
  logoutDeviceApi,
} from "../../../apis/payments.api";
import { useDarkMode } from "../../../hooks/useDarkMode";

const DeviceManagement = () => {
  const { isDarkMode } = useDarkMode();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchDevices();
  }, []);

  const fetchDevices = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await listDevicesApi();
      setDevices(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError("Failed to load devices");
      setDevices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSetCurrent = async (deviceId) => {
    try {
      setActionLoading(deviceId);
      await setCurrentDeviceApi(deviceId);
      await fetchDevices();
      setActionLoading(null);
    } catch (err) {
      setError("Failed to set device as current");
      setActionLoading(null);
    }
  };

  const handleRemoveDevice = async (deviceId) => {
    if (window.confirm("Are you sure you want to remove this device?")) {
      try {
        setActionLoading(deviceId);
        await removeDeviceApi(deviceId);
        await fetchDevices();
        setActionLoading(null);
      } catch (err) {
        setError(err?.response?.data?.error || "Failed to remove device");
        setActionLoading(null);
      }
    }
  };

  const handleLogoutDevice = async (deviceId) => {
    try {
      setActionLoading(deviceId);
      await logoutDeviceApi(deviceId);
      await fetchDevices();
      setActionLoading(null);
    } catch (err) {
      setError("Failed to logout device");
      setActionLoading(null);
    }
  };

  const getDeviceIcon = (deviceType) => {
    const baseClass = "w-8 h-8";
    switch (deviceType?.toLowerCase()) {
      case "mobile":
        return (
          <FaMobile
            className={`${baseClass} ${isDarkMode ? "text-blue-400" : "text-blue-500"}`}
          />
        );
      case "tablet":
        return (
          <FaTabletAlt
            className={`${baseClass} ${isDarkMode ? "text-purple-400" : "text-purple-500"}`}
          />
        );
      case "laptop":
      case "desktop":
        return (
          <FaDesktop
            className={`${baseClass} ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
          />
        );
      default:
        return (
          <FaLaptop
            className={`${baseClass} ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
          />
        );
    }
  };

  if (loading) {
    return (
      <div
        className={`text-center py-8 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
      >
        Loading devices...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div
          className={`bg-${isDarkMode ? "red-900" : "red-50"} border border-${isDarkMode ? "red-700" : "red-200"} text-${isDarkMode ? "red-200" : "red-700"} px-4 py-3 rounded`}
        >
          {error}
        </div>
      )}

      <div className="grid gap-4">
        {devices && devices.length > 0 ? (
          devices.map((device) => (
            <div
              key={device.id}
              className={`flex items-center justify-between p-4 rounded-lg border transition ${
                device.is_current
                  ? `border-${isDarkMode ? "blue-500" : "blue-300"} bg-${isDarkMode ? "blue-900" : "blue-50"}`
                  : `border-${isDarkMode ? "gray-700" : "gray-200"} bg-${isDarkMode ? "gray-800" : "white"} hover:border-${isDarkMode ? "gray-600" : "gray-300"}`
              }`}
            >
              <div className="flex items-center gap-4">
                {getDeviceIcon(device.device_type)}
                <div>
                  <div className="flex items-center gap-2">
                    <p
                      className={`font-semibold ${
                        isDarkMode ? "text-gray-200" : "text-gray-900"
                      }`}
                    >
                      {device.device_name}
                    </p>
                    {device.is_current && (
                      <span
                        className={`px-2 py-1 bg-${isDarkMode ? "blue-800" : "blue-100"} text-${isDarkMode ? "blue-200" : "blue-700"} text-xs font-semibold rounded-full flex items-center gap-1`}
                      >
                        <FaCheck className="w-3 h-3" /> Current
                      </span>
                    )}
                  </div>
                  <p
                    className={`text-sm ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    {device.device_type &&
                      device.device_type.charAt(0).toUpperCase() +
                        device.device_type.slice(1)}{" "}
                    •{" "}
                    {device.last_login_at
                      ? `Last login: ${device.last_login_at}`
                      : "Never logged in"}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {!device.is_current && (
                  <>
                    <button
                      onClick={() => handleSetCurrent(device.id)}
                      disabled={actionLoading === device.id}
                      className={`px-3 py-1 bg-${isDarkMode ? "blue-900" : "blue-50"} text-${isDarkMode ? "blue-200" : "blue-600"} hover:bg-${isDarkMode ? "blue-800" : "blue-100"} text-sm rounded disabled:opacity-50`}
                    >
                      {actionLoading === device.id ? "..." : "Set Current"}
                    </button>
                    <button
                      onClick={() => handleRemoveDevice(device.id)}
                      disabled={actionLoading === device.id}
                      className={`px-3 py-1 text-${isDarkMode ? "red-300" : "red-600"} hover:bg-${isDarkMode ? "red-900" : "red-50"} text-sm rounded disabled:opacity-50`}
                    >
                      {actionLoading === device.id ? (
                        "..."
                      ) : (
                        <FaTimes className="w-4 h-4" />
                      )}
                    </button>
                  </>
                )}
                {device.is_current && (
                  <button
                    onClick={() => handleLogoutDevice(device.id)}
                    disabled={actionLoading === device.id}
                    className={`px-3 py-1 bg-${isDarkMode ? "red-900" : "red-50"} text-${isDarkMode ? "red-200" : "red-600"} hover:bg-${isDarkMode ? "red-800" : "red-100"} text-sm rounded disabled:opacity-50`}
                  >
                    {actionLoading === device.id ? "..." : "Logout"}
                  </button>
                )}
              </div>
            </div>
          ))
        ) : (
          <div
            className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} py-8 text-center`}
          >
            No devices found
          </div>
        )}
      </div>
    </div>
  );
};

export default DeviceManagement;
